<template>
  <div class="bluesky-embed-wrapper my-4" v-if="embedData">
    <v-card 
      variant="flat" 
      class="bluesky-embed-card overflow-hidden border border-white-05 bg-black-05" 
      :href="externalUrl"
      target="_blank"
      @click.stop
    >
      <div class="d-flex flex-column flex-sm-row">
        <!-- Main Content -->
        <div class="pa-4 flex-grow-1">
          <div class="d-flex align-center mb-2">
            <v-icon :icon="typeIcon" size="x-small" :color="typeColor" class="mr-1"></v-icon>
            <span class="text-overline text-grey-lighten-1 font-weight-bold" style="letter-spacing: 0.1em;">
              {{ typeLabel }}
            </span>
          </div>
          <h3 class="text-h5 font-weight-black mb-1 line-clamp-1" style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; letter-spacing: -0.5px;">{{ title }}</h3>
          <div v-if="creator" class="text-caption text-grey-lighten-1 mb-2">
            by @{{ creator.handle }}
          </div>
          <p class="text-caption text-grey-lighten-1 line-clamp-2 mb-0">{{ description }}</p>
        </div>

        <!-- Thumbnail / Avatar -->
        <div v-if="thumbnail" class="embed-thumb flex-shrink-0">
          <v-img :src="thumbnail" cover width="120" height="auto" min-height="100" class="h-100"></v-img>
        </div>
      </div>
    </v-card>
  </div>
  
  <!-- Skeleton Loader -->
  <v-skeleton-loader
    v-else-if="loading"
    type="list-item-three-line"
    class="my-4 rounded-xl bg-black-05 border border-white-05"
  ></v-skeleton-loader>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { atproto } from '@/services/atproto';
import { getOtherProfile } from '@/services/bluesky';

const props = defineProps<{
  url: string;
}>();

const loading = ref(true);
const embedData = ref<any>(null);
const embedType = ref<'feed' | 'starter-pack' | 'list' | null>(null);

const typeLabel = computed(() => {
  switch (embedType.value) {
    case 'feed': return 'BLUESKY FEED';
    case 'starter-pack': return 'STARTER PACK';
    case 'list': return 'USER LIST';
    default: return 'BLUESKY';
  }
});

const typeIcon = computed(() => {
  switch (embedType.value) {
    case 'feed': return 'mdi-rss';
    case 'starter-pack': return 'mdi-package-variant-closed';
    case 'list': return 'mdi-format-list-bulleted';
    default: return 'mdi-link';
  }
});

const typeColor = computed(() => {
    switch (embedType.value) {
    case 'feed': return 'primary';
    case 'starter-pack': return 'amber-darken-2';
    case 'list': return 'blue-lighten-1';
    default: return 'grey';
  }
});

const title = computed(() => {
  if (!embedData.value) return '';
  return embedData.value.displayName || embedData.value.name || 'Untitled';
});

const description = computed(() => {
  if (!embedData.value) return '';
  return embedData.value.description || '';
});

const creator = computed(() => {
  if (!embedData.value) return null;
  return embedData.value.creator || embedData.value.owner;
});

const thumbnail = computed(() => {
  if (!embedData.value) return null;
  return embedData.value.avatar || embedData.value.thumbnail;
});

const externalUrl = computed(() => {
  if (props.url.startsWith('at://')) {
    const parts = props.url.split('/');
    const rkey = parts.pop();
    const collection = parts.pop();
    const did = parts.pop();
    const typePath = collection === 'app.bsky.feed.generator' ? 'feed' : 'lists';
    return `https://bsky.app/profile/${did}/${typePath}/${rkey}`;
  }
  return props.url;
});

const resolveUri = async (url: string) => {
  if (url.startsWith('at://')) {
    if (url.includes('app.bsky.feed.generator')) embedType.value = 'feed';
    else if (url.includes('app.bsky.graph.list')) embedType.value = 'list';
    else if (url.includes('app.bsky.graph.starterpack')) embedType.value = 'starter-pack';
    return url;
  }

  try {
    const urlObj = new URL(url);
    if (urlObj.hostname !== 'bsky.app') return null;

    const pathParts = urlObj.pathname.split('/').filter(p => p);
    
    // Feed: /profile/:handle/feed/:rkey
    if (pathParts[0] === 'profile' && pathParts[2] === 'feed') {
        const handle = pathParts[1];
        const rkey = pathParts[3];
        embedType.value = 'feed';
        const profile = await getOtherProfile(handle);
        if (profile) return `at://${profile.did}/app.bsky.feed.generator/${rkey}`;
        // Fallback for DID in URL
        if (handle.startsWith('did:')) return `at://${handle}/app.bsky.feed.generator/${rkey}`;
    }
    
    // List: /profile/:handle/lists/:rkey
    if (pathParts[0] === 'profile' && pathParts[2] === 'lists') {
        const handle = pathParts[1];
        const rkey = pathParts[3];
        embedType.value = 'list';
        const profile = await getOtherProfile(handle);
        if (profile) return `at://${profile.did}/app.bsky.graph.list/${rkey}`;
        if (handle.startsWith('did:')) return `at://${handle}/app.bsky.graph.list/${rkey}`;
    }

    // Starter Pack: /starter-pack/:handle/:rkey
    if (pathParts[0] === 'starter-pack') {
        const handle = pathParts[1];
        const rkey = pathParts[2];
        embedType.value = 'starter-pack';
        const profile = await getOtherProfile(handle);
        if (profile) return `at://${profile.did}/app.bsky.graph.starterpack/${rkey}`;
        if (handle.startsWith('did:')) return `at://${handle}/app.bsky.graph.starterpack/${rkey}`;
    }
  } catch (e) {
    console.warn('Failed to parse URL for Bluesky embed:', e);
  }
  return null;
};

const fetchData = async () => {
  loading.value = true;
  try {
    const atUri = await resolveUri(props.url);
    if (!atUri) {
        loading.value = false;
        return;
    }

    if (embedType.value === 'feed') {
        embedData.value = await atproto.getFeedGenerator(atUri);
    } else if (embedType.value === 'starter-pack') {
        embedData.value = await atproto.getStarterPack(atUri);
    } else if (embedType.value === 'list') {
        embedData.value = await atproto.getList(atUri);
    }
  } catch (error) {
    console.error('Failed to fetch Bluesky embed data:', error);
  } finally {
    loading.value = false;
  }
};

onMounted(fetchData);
</script>

<script lang="ts">
export const extractBlueskyLinks = (text: string) => {
  if (!text) return [];
  const results: string[] = [];
  
  // Regex for Bluesky special links
  // Feeds: bsky.app/profile/*/feed/*
  // Lists: bsky.app/profile/*/lists/*
  // Starter Packs: bsky.app/starter-pack/*/*
  const bskyRegex = /https:\/\/bsky\.app\/(profile\/[^\/\s]+\/(feed|lists)\/[^\/\s\?#]+|starter-pack\/[^\/\s]+\/[^\/\s\?#]+)/g;
  
  let match;
  while ((match = bskyRegex.exec(text)) !== null) {
      results.push(match[0]);
  }
  
  return [...new Set(results)]; // Deduplicate
};
</script>

<style scoped>
.bluesky-embed-wrapper {
  max-width: 600px;
}

.bluesky-embed-card {
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  text-decoration: none;
}

.bluesky-embed-card:hover {
  background: rgba(var(--v-theme-primary), 0.05) !important;
  border-color: rgba(var(--v-theme-primary), 0.3) !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.embed-thumb {
  width: 120px;
  background: rgba(0, 0, 0, 0.3);
}

@media (max-width: 600px) {
  .embed-thumb {
    width: 100%;
    height: 100px;
  }
}

.line-clamp-1 {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.bg-black-05 {
  background: rgba(0, 0, 0, 0.2) !important;
}

.border-white-05 {
  border-color: rgba(255, 255, 255, 0.05) !important;
}
</style>
